import { useMemo, useState } from "react";
import StudyInformationCard from "./components/StudyDash/StudyInformationCard";
import PatientsOverviewCard from "./components/StudyDash/PatientsOverviewCard";
import StudyGraphComponent from "./components/StudyDash/StudyGraphComponent";
import ViewPatientsModal from "./components/Modals/ViewPatientsModal";
import NoStudiesMessage from "./components/NoStudiesMessage";

import "./StudyDashboard.css";
import "../../overall.css";
import TitleBar from "../../components/common/TitleBar";
import DashBoardDropDown from "./DashBoardDropDown";
import InvitePatientModal from "../../components/invitePatientModal/InvitePatientModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NewStudyModal from "../HomeDiary/Modals/NewStudyModal/NewStudyModal";
import { getAllPatients } from "../../API/PatientDirectoryCalls";

interface Study {
  abstract: String;
  name: String;
  patients: {
    $id: String;
    $values: Array<Patient>;
  };
  studyID: Number;
}

type StudyDashProps = {
  patients: Array<Patient>;
  allStudyData: Study[];
  dashBoardStudyData: any;
};

type Patient = {
  $id: string;
  givenName: string;
  surname: string;
  userName: string;
  patientID: number;
};

function StudyDashboard(data: StudyDashProps) {
  const [showPatientsModal, setShowPatientsModal] = useState(false);
  const [study, setStudy] = useState<Study>(data.allStudyData[0]);
  const [studyIndex, setStudyIndex] = useState<number>(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newStudyModalOpen, setNewStudyModalOpen] = useState(false);
  const [patients, setPatients] = useState<Array<Patient>>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false); // ADD loading state
  const [
    showInviteModalWithoutAllPatients,
    setShowInviteModalWithoutAllPatients,
  ] = useState(false);

  const dropdown = useMemo(
    () => (
      <DashBoardDropDown
        setStudyIndex={setStudyIndex}
        setStudy={setStudy}
        study={study}
        studyIndex={studyIndex}
        studies={data.allStudyData}
      />
    ),
    [study, studyIndex, data.allStudyData]
  );

  const handlePostStudy = () => {
    // Only reload the page when called (from modal close)
    window.location.reload();
  };

  const handleCreateStudyClick = async () => {
    setIsLoadingPatients(true); // Start loading
    try {
      const patientsList = await getAllPatients();
      setPatients(patientsList || []);
      setNewStudyModalOpen(true);
    } catch (error) {
      console.error("Failed to load patients:", error);
      toast.error("Unable to load patients. Please try again later.");
    } finally {
      setIsLoadingPatients(false); // Stop loading
    }
  };

  const handleNewStudyModalClose = () => {
    setNewStudyModalOpen(false);
    setPatients([]); // Clear patients so fresh data is loaded next time
  };

  // Show "No Studies" message when there are no studies
  if (data.allStudyData.length === 0) {
    return (
      <>
        <div className="parentContainer study-dashboard">
          <div className="mainContainer">
            <TitleBar title={"Study Dashboard"} />
            <NoStudiesMessage
              onCreateStudy={handleCreateStudyClick}
              isLoading={isLoadingPatients} // Pass loading state
            />
          </div>
        </div>

        <NewStudyModal
          newStudyModalOpen={newStudyModalOpen}
          setNewStudyModalOpen={handleNewStudyModalClose}
          patients={patients}
          handlePostStudy={handlePostStudy}
        />
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <div className="parentContainer study-dashboard">
        <div className="mainContainer">
          {/* Rule in app.css */}
          {/*Send props to change title bar depending on page */}
          <TitleBar title={"Study Dashboard"} studyDropdown={dropdown} />
          {/*Send props to change title bar depending on page */}
          <div>
            <div className="studyInformation row">
              <StudyInformationCard
                studyIndex={studyIndex}
                studies={data.allStudyData}
                setViewPatientsModalOpen={setShowPatientsModal}
                studyInfo={data.dashBoardStudyData.studies}
                studyId={study?.studyID}
              />
              <PatientsOverviewCard
                studyIndex={studyIndex}
                studies={data.allStudyData}
                studyInfo={data.dashBoardStudyData.studies}
                studyId={study?.studyID}
                openViewAllPatients={() => setShowPatientsModal(true)}
                openInviteModal={() =>
                  setShowInviteModalWithoutAllPatients(true)
                }
              />
            </div>
            <StudyGraphComponent studyId={study?.studyID} />
          </div>
        </div>
        <ViewPatientsModal
          onClose={() => setShowPatientsModal(false)}
          show={showPatientsModal}
          study={data.allStudyData[studyIndex as number]}
          setShowInviteModal={() => {
            setShowInviteModal(true);
          }}
        />

        <InvitePatientModal
          show={showInviteModal || showInviteModalWithoutAllPatients}
          studies={data.allStudyData}
          studyData={data.dashBoardStudyData.studies}
          onShowNotificationSuccess={() => {
            toast("Email has been sent!");
          }}
          onClose={() => {
            setShowInviteModal(false);
            if (!showInviteModalWithoutAllPatients) {
              setShowPatientsModal(true);
            }
            setShowInviteModalWithoutAllPatients(false);
          }}
        />
      </div>

      <NewStudyModal
        newStudyModalOpen={newStudyModalOpen}
        setNewStudyModalOpen={handleNewStudyModalClose}
        patients={patients}
        handlePostStudy={handlePostStudy}
      />
      <ToastContainer />
    </>
  );
}
export default StudyDashboard;
